import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter, useLocalSearchParams, Stack, useFocusEffect } from "expo-router";
import { useCallback, useState } from "react";
import { StyleSheet, View, TouchableOpacity, ActivityIndicator } from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DocumentStep } from "@/components/onboarding/identity/DocumentStep";
import { SelfieStep } from "@/components/onboarding/identity/SelfieStep";
import { VerificationStep } from "@/components/onboarding/identity/VerificationStep";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { MOBILE_DEBUG_VERSION } from "@/lib/debug";
import { resolveIdentityItems } from "@/lib/identity-items";
import { resolveIdentityResumeState } from "@/lib/identity-resume";
import {
    fetchOnboardingState,
    submitUpload,
    confirmUpload,
    triggerIdentityVerification,
    type ObItemWithSubmission
} from "@/lib/onboarding-api";
import { colors, fonts, gradients, radius, shadows, spacing, typography } from "@/lib/theme";
import { uploadFromUri } from "@/lib/upload-from-uri";

const STEPS = ["selfie", "document", "verification"] as const;

const STEP_LABELS = ["Take Selfie", "Upload ID", "Verification"];
const STEP_ICONS: Array<keyof typeof MaterialCommunityIcons.glyphMap> = [
    "camera-account",
    "card-account-details-outline",
    "shield-check-outline"
];

export default function IdentityVerificationScreen() {
    const router = useRouter();
    const { rnId, sectionId, subsectionId } = useLocalSearchParams<{
        rnId: string;
        sectionId: string;
        subsectionId: string;
    }>();

    const [stepIdx, setStepIdx] = useState(0);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    const [selfieItem, setSelfieItem] = useState<ObItemWithSubmission | null>(null);
    const [documentItem, setDocumentItem] = useState<ObItemWithSubmission | null>(null);
    const [selfieUri, setSelfieUri] = useState<string | null>(null);
    const [documentUri, setDocumentUri] = useState<string | null>(null);
    const [selfieItemId, setSelfieItemId] = useState<string>("");
    const [approved, setApproved] = useState(false);
    const { dialogState, showDialog, dismissDialog } = useConfirmDialog();

    const resolvedRnId = rnId ?? "";
    const onboardingHref = resolvedRnId ? `/onboarding?rnId=${resolvedRnId}` : "/onboarding";
    const sectionHref = sectionId
        ? `/onboarding/section/${sectionId}?rnId=${resolvedRnId}`
        : onboardingHref;

    useFocusEffect(
        useCallback(() => {
            if (!resolvedRnId || !subsectionId) {
                return;
            }

            async function loadState() {
                setLoading(true);
                try {
                    const state = await fetchOnboardingState(resolvedRnId);
                    let selfie: ObItemWithSubmission | null = null;
                    let document: ObItemWithSubmission | null = null;

                    //  `aws_face` is the ONLY discriminator for the selfie; the
                    //  item type is incidental (camera capture and file upload
                    //  both produce an image). Requiring `itemType === "camera"`
                    //  matched nothing in production, where BOTH items are
                    //  `document_upload` — so `selfie` stayed null, the guard
                    //  below returned early, and neither item id was ever set.
                    //  Every upload then went up with `submissionId: undefined`
                    //  and was stored orphaned, so the verify step reported
                    //  "Both selfie and photo ID must be uploaded" over files
                    //  that had in fact reached S3. Same resolution the server
                    //  uses in onboarding-verify-identity.service.ts.
                    for (const section of state.config) {
                        const sub = section.subsections.find((s) => String(s.id) === subsectionId);
                        if (sub) {
                            const resolved = resolveIdentityItems(sub.items);
                            selfie = resolved.selfie;
                            document = resolved.document;
                            break;
                        }
                    }

                    if (!selfie || !document) {
                        return;
                    }
                    setSelfieItem(selfie);
                    setDocumentItem(document);
                    setSelfieItemId(selfie.id);

                    const selfieUpload = selfie.submission?.uploads?.find((u) => u.isCurrent);
                    const documentUpload = document.submission?.uploads?.find((u) => u.isCurrent);
                    if (selfieUpload) {
                        setSelfieUri(selfieUpload.viewUrl ?? "uploaded");
                    }
                    if (documentUpload) {
                        setDocumentUri(documentUpload.viewUrl ?? "uploaded");
                    }

                    // Resume logic — see resolveIdentityResumeState. The flow is
                    // complete only when BOTH the selfie and the photo ID are
                    // approved; a rejection returns her to the step that can fix it.
                    const selfieStatus = selfie.submission?.status ?? "required";
                    const documentStatus = document.submission?.status ?? "required";
                    const resume = resolveIdentityResumeState(selfieStatus, documentStatus);
                    setApproved(resume.approved);
                    setStepIdx(resume.stepIdx);
                } catch {
                    // Stay at step 0
                } finally {
                    setLoading(false);
                }
            }

            loadState();
        }, [resolvedRnId, subsectionId])
    );

    async function uploadImage(
        uri: string,
        filenamePrefix: string,
        submissionId: string | undefined
    ) {
        // Always use JPEG — expo-image-picker at quality < 1.0 outputs JPEG.
        // Fixes blob URL bug (#15) where URI parsing produced garbage extensions.
        const filename = `${filenamePrefix}_${Date.now()}.jpg`;
        const contentType = "image/jpeg";

        //  Refuse to upload something nothing can own. Without this the row is
        //  written with a NULL submission_id: S3 gets the image, the submission
        //  stays empty, and the failure only surfaces later as "Both selfie and
        //  photo ID must be uploaded" — pointing at the wrong step entirely.
        //  Six such orphans were created in production before this was caught.
        if (!submissionId) {
            throw new Error(
                "This step isn't ready yet. Go back and reopen Identity Verification, then try again."
            );
        }

        const { upload, uploadUrl } = await submitUpload(resolvedRnId, {
            filename,
            contentType,
            submissionId
        });

        //  Use the shared uploader rather than fetch(uri) -> blob() -> PUT.
        //
        //  On native it sends the file through expo-file-system's upload task
        //  instead of materialising a Blob, which is the path that actually
        //  works on Android 11+ (there, `fetch(file://)` rejects with status 0),
        //  and it carries the timeout and the cancel-resolves-undefined
        //  handling this screen never had.
        //
        //  This screen was the only place still hand-rolling the upload, and it
        //  is the only place losing files: across five days of production
        //  uploads, 22 of 24 selfies and 5 of 8 photo IDs pointed at objects
        //  that do not exist in S3, while every other item — RN Physical, TB,
        //  references, insurance, immunisations — was intact at 0 of 36.
        await uploadFromUri({ uri, uploadUrl, contentType });
        await confirmUpload(resolvedRnId, upload.id);
        return upload;
    }

    async function handleNext() {
        const currentStep = STEPS[stepIdx];
        if (currentStep === "selfie") {
            if (!selfieUri) {
                showDialog("Selfie Required", "Please take a selfie before continuing.");
                return;
            }
            // Skip upload if already uploaded from a previous session (not a new local capture)
            if (selfieUri === "uploaded" || selfieUri.startsWith("http")) {
                setStepIdx(1);
                return;
            }
            setUploading(true);
            try {
                await uploadImage(selfieUri, "selfie", selfieItem?.submission?.id);
                setStepIdx(1);
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : "Please try again.";
                showDialog("Upload Failed", message);
            } finally {
                setUploading(false);
            }
        } else if (currentStep === "document") {
            if (!documentUri) {
                showDialog(
                    "Document Required",
                    "Please upload your government ID before continuing."
                );
                return;
            }
            // Skip upload if already uploaded from a previous session
            if (documentUri === "uploaded" || documentUri.startsWith("http")) {
                setUploading(true);
                try {
                    await triggerIdentityVerification(resolvedRnId);
                    setStepIdx(2);
                } catch (err: unknown) {
                    const message = err instanceof Error ? err.message : "Please try again.";
                    showDialog("Verification Failed", message);
                } finally {
                    setUploading(false);
                }
                return;
            }
            setUploading(true);
            try {
                await uploadImage(documentUri, "document", documentItem?.submission?.id);
                await triggerIdentityVerification(resolvedRnId);
                setStepIdx(2);
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : "Please try again.";
                showDialog("Upload Failed", message);
            } finally {
                setUploading(false);
            }
        }
    }

    function handleBack() {
        console.info(`[mobile ${MOBILE_DEBUG_VERSION}] identity back pressed`, {
            stepIdx,
            sectionHref
        });
        // Once verification is done, Back exits to the section rather than
        // replaying the already-completed selfie / ID / verify steps.
        if (approved || stepIdx === 0) {
            router.navigate(sectionHref);
        } else {
            setStepIdx(stepIdx - 1);
        }
    }

    if (loading) {
        return (
            <View style={styles.center}>
                <ActivityIndicator size="large" color={colors.teal400} />
            </View>
        );
    }

    const currentStep = STEPS[stepIdx];
    const isVerificationStep = currentStep === "verification";

    return (
        <>
            <Stack.Screen options={{ headerShown: false }} />
            <SafeAreaView style={styles.safeArea} edges={["top", "bottom"]}>
                <View style={styles.content}>
                    <View style={styles.topBar}>
                        <TouchableOpacity onPress={handleBack} style={styles.topBarBackButton}>
                            <MaterialCommunityIcons
                                name="chevron-left"
                                size={28}
                                color={colors.textPrimary}
                            />
                        </TouchableOpacity>
                        <Text style={styles.topBarTitle}>Identity Verification</Text>
                        <View style={styles.topBarSpacer} />
                    </View>

                    {/* Step indicator: 3 circles with connecting lines */}
                    <View style={styles.stepIndicatorRow}>
                        {STEPS.map((step, idx) => {
                            const isActive = idx === stepIdx;
                            const isCompleted = idx < stepIdx;
                            return (
                                <View key={step} style={styles.stepItemWrap}>
                                    {/* Connecting line (left) */}
                                    {idx > 0 && (
                                        <View
                                            style={[
                                                styles.stepLine,
                                                styles.stepLineLeft,
                                                isCompleted && styles.stepLineCompleted
                                            ]}
                                        />
                                    )}

                                    {/* Circle */}
                                    {isActive ? (
                                        <LinearGradient
                                            colors={[...gradients.brand]}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 1, y: 1 }}
                                            style={styles.stepCircleActive}
                                        >
                                            <MaterialCommunityIcons
                                                name={STEP_ICONS[idx]}
                                                size={18}
                                                color={colors.surface}
                                            />
                                        </LinearGradient>
                                    ) : isCompleted ? (
                                        <View style={styles.stepCircleCompleted}>
                                            <MaterialCommunityIcons
                                                name="check"
                                                size={16}
                                                color={colors.surface}
                                            />
                                        </View>
                                    ) : (
                                        <View style={styles.stepCircleInactive}>
                                            <MaterialCommunityIcons
                                                name={STEP_ICONS[idx]}
                                                size={16}
                                                color={colors.textTertiary}
                                            />
                                        </View>
                                    )}

                                    {/* Connecting line (right) */}
                                    {idx < STEPS.length - 1 && (
                                        <View
                                            style={[
                                                styles.stepLine,
                                                styles.stepLineRight,
                                                isCompleted && styles.stepLineCompleted
                                            ]}
                                        />
                                    )}
                                </View>
                            );
                        })}
                    </View>

                    {/* Step label */}
                    <View style={styles.stepLabel}>
                        <Text style={styles.stepLabelText}>
                            Step {stepIdx + 1}: {STEP_LABELS[stepIdx]}
                        </Text>
                    </View>

                    {/* Step content */}
                    <View style={styles.stepContent}>
                        {currentStep === "selfie" && (
                            <SelfieStep imageUri={selfieUri} onCapture={setSelfieUri} />
                        )}
                        {currentStep === "document" && (
                            <DocumentStep
                                imageUri={documentUri}
                                onCapture={setDocumentUri}
                                onRemove={() => setDocumentUri(null)}
                            />
                        )}
                        {currentStep === "verification" &&
                            selfieItem?.submission?.id &&
                            documentItem?.submission?.id && (
                                <VerificationStep
                                    rnId={resolvedRnId}
                                    // These carry the ITEM ids: VerificationStep.checkStatus matches
                                    // them against status.submissions[].itemId (submission ids never
                                    // match itemId, which left the loader stuck forever).
                                    selfieSubmissionId={String(selfieItem.id)}
                                    documentSubmissionId={String(documentItem.id)}
                                    initialResult={approved ? "approved" : "pending"}
                                    onStartOver={() => {
                                        setStepIdx(0);
                                        setSelfieUri(null);
                                        setDocumentUri(null);
                                        setApproved(false);
                                    }}
                                    onApproved={() => setApproved(true)}
                                />
                            )}
                    </View>

                    {/* Footer: Next button */}
                    {!isVerificationStep && !approved && (
                        <View style={styles.footer}>
                            <TouchableOpacity
                                onPress={handleNext}
                                disabled={uploading}
                                activeOpacity={0.8}
                            >
                                <LinearGradient
                                    colors={[...gradients.brand]}
                                    start={{ x: 0, y: 0 }}
                                    end={{ x: 1, y: 0 }}
                                    style={[
                                        styles.nextButton,
                                        uploading && { opacity: 0.7 },
                                        shadows.brand
                                    ]}
                                >
                                    {uploading ? (
                                        <ActivityIndicator size="small" color={colors.surface} />
                                    ) : (
                                        <>
                                            <Text style={styles.nextButtonText}>Next</Text>
                                            <MaterialCommunityIcons
                                                name="arrow-right"
                                                size={18}
                                                color={colors.surface}
                                            />
                                        </>
                                    )}
                                </LinearGradient>
                            </TouchableOpacity>
                        </View>
                    )}

                    {/* Footer: Done button (approved) */}
                    {approved && (
                        <View style={styles.footer}>
                            <TouchableOpacity
                                onPress={() => router.navigate(sectionHref)}
                                activeOpacity={0.8}
                            >
                                <View style={[styles.doneButton, shadows.teal]}>
                                    <Text style={styles.nextButtonText}>Done</Text>
                                    <MaterialCommunityIcons
                                        name="check"
                                        size={18}
                                        color={colors.surface}
                                    />
                                </View>
                            </TouchableOpacity>
                        </View>
                    )}
                </View>
                <ConfirmDialog {...dialogState} onDismiss={dismissDialog} />
            </SafeAreaView>
        </>
    );
}

const styles = StyleSheet.create({
    safeArea: {
        flex: 1,
        backgroundColor: colors.background
    },
    center: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center"
    },
    content: {
        flex: 1
    },
    topBar: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: spacing.base,
        paddingTop: spacing.xs,
        paddingBottom: spacing.sm
    },
    topBarBackButton: {
        width: 44,
        height: 44,
        borderRadius: 12,
        alignItems: "center",
        justifyContent: "center",
        marginLeft: -10
    },
    topBarTitle: {
        flex: 1,
        textAlign: "center",
        fontSize: 18,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    topBarSpacer: {
        width: 34
    },

    // ── Step indicator ──
    stepIndicatorRow: {
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        paddingTop: spacing.base,
        paddingBottom: spacing.xs,
        paddingHorizontal: spacing["3xl"]
    },
    stepItemWrap: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center"
    },
    stepCircleActive: {
        width: 40,
        height: 40,
        borderRadius: 20,
        alignItems: "center",
        justifyContent: "center"
    },
    stepCircleCompleted: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: colors.success500,
        alignItems: "center",
        justifyContent: "center"
    },
    stepCircleInactive: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: colors.surface,
        borderWidth: 2,
        borderColor: colors.border,
        alignItems: "center",
        justifyContent: "center"
    },
    stepLine: {
        flex: 1,
        height: 2,
        backgroundColor: colors.border
    },
    stepLineLeft: {
        marginRight: spacing.xs
    },
    stepLineRight: {
        marginLeft: spacing.xs
    },
    stepLineCompleted: {
        backgroundColor: colors.success500
    },

    // ── Step label ──
    stepLabel: {
        alignItems: "center",
        paddingBottom: spacing.sm
    },
    stepLabelText: {
        ...typography.caption,
        color: colors.textTertiary
    },

    // ── Step content ──
    stepContent: {
        flex: 1,
        paddingHorizontal: spacing.base
    },

    // ── Footer ──
    footer: {
        padding: spacing.base,
        paddingBottom: spacing.sm,
        borderTopWidth: 1,
        borderTopColor: colors.border
    },
    nextButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        borderRadius: radius.full,
        paddingVertical: 16
    },
    nextButtonText: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.surface
    },
    doneButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        backgroundColor: colors.success500,
        borderRadius: radius.full,
        paddingVertical: 16
    }
});
